import React, { useEffect, useState } from "react";
import styled from "styled-components";
import GenericError from "components/GenericError";
import { ButtonRow, SeparateLine } from "styles/elements/main";
import { EditHandler } from "modules/drivers/components";
import changedForm from "hooks/useChangedForm";
import trackAnalytics from "trackAnalytics";
import { FormGroup, Row } from "styles/forms/main";
import Api from "lib/api";
import { AccordionComponent, Button, Input, RejectionReason, SelectWithSearch } from "components";
import Spinner from "components/Spinner";
import { getAllBoxTypes, getAllSuppliers, getAllTruckTypes } from "../api";
// eslint-disable-next-line import/no-cycle
import { LicenseInfo, TruckInfo } from ".";
import { mapTruckDataToApi } from "../config/mapUiToData";
import useFetchTruck from "../config/useFetchTruck";
import RejectionList from "components/rejectionList";
import { useSelector } from "react-redux";
import { authorizedActionsSelector } from "modules/auth/slice";
import checkAuthorization from "modules/auth/helpers/checkAuthorization";
import { getUserRoles } from "modules/auth/slice";

const Container = styled.div`
    margin-bottom: 27px;
`;
function EditTruck({ onClose, selectedTruckId, onSuccess, isSuppliersEntity }) {
    const [isEditingTruckInfo, setIsEditingTruckInfo] = useState(false);
    const [isEditingLicenseInfo, setIsEditingLicenseInfo] = useState(false);
    const [isEditingSupplyInfo, setIsEditingSupplyInfo] = useState(false);
    const [isEditingOthers, setIsEditingOthers] = useState(false);
    const [truckData, setTruckData] = useState(null);
    const [allTruckClass, setTruckClass] = useState(null);
    const [allSuppliers, setAllSuppliers] = useState(null);
    const [allBoxesType, setAllBoxesType] = useState(null);
    const [isFormValid, setIsFormValid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState(null);
    const getCurrentUserRoles = useSelector(getUserRoles());
    const isSupplierPartner = getCurrentUserRoles?.includes("supplier_partner_account_manager");
    const { selectedTruckData, truckAttachments, setTruckAttachments, objections } = useFetchTruck(
        selectedTruckId,
        `${
            isSupplierPartner ? "suppliers" : "supplier"
        },truckType,attachments,objections,objections.section,objections.item`
    );
    const [isPlateNumberEditable, setIsPlateNumberEditable] = useState(false);
    const authorizedActions = useSelector(authorizedActionsSelector());
    const isAuthorizedToCreate = checkAuthorization(authorizedActions, "trucks", "create");
    const handleGetTruckClass = async () => {
        const data = await getAllTruckTypes();
        setTruckClass(data.map((name) => ({ value: truckData?.truckType?.value, label: name })));
    };
    const handleGetSuppliers = async () => {
        const data = await getAllSuppliers();
        setAllSuppliers(data);
    };

    const handleGetBoxTypes = async (truckClass) => {
        const data = await getAllBoxTypes(truckClass);
        setAllBoxesType(data);
    };
    const handleEditTruck = async (e) => {
        e.preventDefault();
        try {
            setIsLoading(true);
            const changedData = changedForm(selectedTruckData, truckData);
            trackAnalytics("truck_profile_submit_edit", changedData);
            const response = await Api.put(
                `trucks/${selectedTruckId}`,
                await mapTruckDataToApi(truckData, selectedTruckId, isSupplierPartner)
            );
            setIsLoading(false);
            onSuccess(truckData.plateNumber);
        } catch (error) {
            setIsLoading(false);
            setErrors(error?.errors);
            if (error?.errors?.base?.[0] ?? "" === "invalid plate number format") {
                setIsPlateNumberEditable(true);
            }
            return error;
        }
    };
    

    useEffect(() => {
        setTruckData(selectedTruckData);
    }, [selectedTruckData]);
    useEffect(() => {
        if (truckData?.truckClass) {
            handleGetBoxTypes(truckData.truckClass.label);
        } else {
            setAllBoxesType([]);
        }
    }, [truckData?.truckClass]);

    useEffect(() => {
        handleGetTruckClass();
        handleGetSuppliers();
    }, []);

    useEffect(() => {
        if (
            truckData?.truckClass === undefined ||
            truckData?.truckType === null ||
            truckData?.plateNumber === "" ||
            truckData?.year == null ||
            truckData?.licenseFace?.length === 0 ||
            truckData?.licenseBack?.length === 0 ||
            truckData?.licenseExpirationDate == null
        ) {
            setIsFormValid(false);
        } else {
            setIsFormValid(true);
        }
    }, [truckData]);
    return (
        <>
            {Object.keys(objections).length > 0 && isAuthorizedToCreate && (
                <RejectionList objections={objections} />
            )}
            <AccordionComponent
                title="Truck Info"
                defaultExpanded
                extraAction={
                    <EditHandler
                        isOnEdit={isEditingTruckInfo}
                        setIsOnEdit={setIsEditingTruckInfo}
                    />
                }
                body={
                    <TruckInfo
                        newTruckData={truckData}
                        setNewTruckData={setTruckData}
                        allTruckClass={allTruckClass}
                        allBoxesType={allBoxesType}
                        isEditable={isEditingTruckInfo}
                        errors={errors?.[0]}
                        selectedTruckId={selectedTruckId}
                        isPlateNumberEditable={isPlateNumberEditable}
                        objections={objections["Truck Info - Truck"] ?? null}
                        isAuthorizedToCreate={isAuthorizedToCreate}
                    />
                }
            />
            <SeparateLine />

            <AccordionComponent
                title="License Info"
                defaultExpanded
                extraAction={
                    <EditHandler
                        isOnEdit={isEditingLicenseInfo}
                        setIsOnEdit={setIsEditingLicenseInfo}
                    />
                }
                body={
                    <LicenseInfo
                        newTruckData={truckData}
                        setNewTruckData={setTruckData}
                        isEditable={isEditingLicenseInfo}
                        selectedTruckId={selectedTruckId}
                        truckAttachments={truckAttachments}
                        setTruckAttachments={setTruckAttachments}
                        objections={objections["License Info - Truck"] ?? null}
                        isAuthorizedToCreate={isAuthorizedToCreate}
                    />
                }
            />
            <SeparateLine />

            <AccordionComponent
                title="Supplier Info"
                defaultExpanded
                extraAction={
                    !isSuppliersEntity && (
                        <EditHandler
                            isOnEdit={isEditingSupplyInfo}
                            setIsOnEdit={setIsEditingSupplyInfo}
                        />
                    )
                }
                body={
                    <FormGroup>
                        <Row>
                            <SelectWithSearch
                                label="Supplier"
                                isEditable={isEditingSupplyInfo}
                                isMulti={isSupplierPartner}
                                options={allSuppliers}
                                handleChange={(value) => {
                                    setTruckData({
                                        ...truckData,
                                        ...(!isSupplierPartner
                                            ? { supplierName: value }
                                            : { suppliers: value }),
                                    });
                                }}
                                handleFilter={(filter) => getAllSuppliers(filter)}
                                value={
                                    isSupplierPartner
                                        ? truckData?.suppliers
                                        : truckData?.supplierName
                                }
                            />
                            {objections &&
                                objections["Supplier Info - Truck"] &&
                                objections["Supplier Info - Truck"]["Supplier"] &&
                                isAuthorizedToCreate && (
                                    <RejectionReason
                                        content={objections["Supplier Info - Truck"]["Supplier"]}
                                    />
                                )}
                        </Row>
                    </FormGroup>
                }
            />
            <SeparateLine />

            <AccordionComponent
                title="Others"
                defaultExpanded
                extraAction={
                    <EditHandler
                        isOnEdit={isEditingOthers}
                        setIsOnEdit={setIsEditingOthers}
                    />
                }
                body={
                    <Container>
                        <Input
                            label="Notes"
                            isEditable={isEditingOthers}
                            onChange={(e) =>
                                setTruckData({
                                    ...truckData,
                                    notes: e.target.value,
                                })
                            }
                            value={truckData?.notes}
                        />
                    </Container>
                }
            />
            {errors && <GenericError errorMassege={errors} />}

            <ButtonRow>
                <Button
                    onChange={onClose}
                    name="Cancel"
                    btnStyle="secondBtn"
                />
                <Button
                    type="submit"
                    name={isLoading ? <Spinner /> : "Submit"}
                    onChange={handleEditTruck}
                    btnStyle={isFormValid ? "primaryBtn" : "disabled"}
                    disabled={!isFormValid}
                />
            </ButtonRow>
        </>
    );
}

export default EditTruck;
